import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import RepeatableFieldList from '../../../molecules/RepeatableFieldList';
import {
    COMMODITY_CULTURE_OPTIONS,
    LEGAL_REGIME_OPTIONS,
    VERIFICATION_TYPE_OPTIONS,
    VERIFICATION_STATUS_OPTIONS,
    AREA_UNIT_OPTIONS,
    MAX_OWNERS,
} from '../../../../types/Commodity';
import type {
    CommodityOwner,
    CommodityDelivery,
    CommodityVerification,
    LegalRegime as LegalRegimeValue,
} from '../../../../types/Commodity';
import type { ErrorField } from '../../../../types/ErrorField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';

const UF_OPTIONS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']
    .map(uf => ({ value: uf, label: uf }));

interface CommodityFormProps {
    errorFields: ErrorField[];
    name: string; setName: (v: string) => void;
    description: string; setDescription: (v: string) => void;
    legalRegimes: LegalRegimeValue[]; toggleLegalRegime: (r: LegalRegimeValue) => void;
    custodyAddress: string; setCustodyAddress: (v: string) => void;
    custodyCity: string; setCustodyCity: (v: string) => void;
    custodyState: string; setCustodyState: (v: string) => void;
    farmName: string; setFarmName: (v: string) => void;
    plantedArea: number | ''; setPlantedArea: (v: number | '') => void;
    areaUnit: string; setAreaUnit: (v: string) => void;
    areaOwner: string; setAreaOwner: (v: string) => void;
    leasedArea: boolean; setLeasedArea: (v: boolean) => void;
    registrationNumber: string; setRegistrationNumber: (v: string) => void;
    registryOffice: string; setRegistryOffice: (v: string) => void;
    registryState: string; setRegistryState: (v: string) => void;
    warehouseNotified: boolean; setWarehouseNotified: (v: boolean) => void;
    comments: string; setComments: (v: string) => void;
    deliveries: CommodityDelivery[];
    addDelivery: () => void; removeDelivery: (i: number) => void;
    updateDelivery: (i: number, patch: Partial<CommodityDelivery>) => void;
    verifications: CommodityVerification[];
    addVerification: () => void; removeVerification: (i: number) => void;
    updateVerification: (i: number, patch: Partial<CommodityVerification>) => void;
    owners: CommodityOwner[];
    removeOwner: (i: number) => void;
    ownerSearch: string; setOwnerSearch: (v: string) => void;
    ownerResults: any[]; isSearchingOwner: boolean; showOwnerDropdown: boolean;
    searchOwner: (query: string) => void; handleSelectOwner: (person: any) => void;
}

const CommodityForm = ({
    errorFields,
    name, setName,
    description, setDescription,
    legalRegimes, toggleLegalRegime,
    custodyAddress, setCustodyAddress,
    custodyCity, setCustodyCity,
    custodyState, setCustodyState,
    farmName, setFarmName,
    plantedArea, setPlantedArea,
    areaUnit, setAreaUnit,
    areaOwner, setAreaOwner,
    leasedArea, setLeasedArea,
    registrationNumber, setRegistrationNumber,
    registryOffice, setRegistryOffice,
    registryState, setRegistryState,
    warehouseNotified, setWarehouseNotified,
    comments, setComments,
    deliveries, addDelivery, removeDelivery, updateDelivery,
    verifications, addVerification, removeVerification, updateVerification,
    owners, removeOwner,
    ownerSearch, setOwnerSearch,
    ownerResults, isSearchingOwner, showOwnerDropdown,
    searchOwner, handleSelectOwner,
}: CommodityFormProps) => {
    return (
        <div className="p-4 space-y-6 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            {/* Dados dos Grãos */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Dados dos Grãos</h4>
                <div className="grid grid-cols-2 gap-3">
                    <TextEdit label="Nome / Identificação" id="com-name" value={name}
                        onChange={e => setName(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'name')} />
                    <TextEdit label="Descrição *" id="com-description" value={description}
                        onChange={e => setDescription(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'description')} />
                </div>

                <div className="mt-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Regime(s) Jurídico(s) *</span>
                    <div className="flex flex-wrap gap-3 mt-1">
                        {LEGAL_REGIME_OPTIONS.map(opt => (
                            <label key={opt.value} className="flex items-center gap-1 text-sm">
                                <input
                                    type="checkbox"
                                    checked={legalRegimes.includes(opt.value)}
                                    onChange={() => toggleLegalRegime(opt.value)}
                                />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                    {getErrorMessage(errorFields, 'legalRegimes') && (
                        <div className="mt-1 text-xs text-red-600">{getErrorMessage(errorFields, 'legalRegimes')}</div>
                    )}
                </div>
            </section>

            {/* Entregas (safra/cultura) */}
            <section>
                <RepeatableFieldList<CommodityDelivery>
                    id="commodity-deliveries"
                    label="Entregas (cultura / safra) *"
                    items={deliveries}
                    onAdd={addDelivery}
                    onRemove={removeDelivery}
                    onUpdate={updateDelivery}
                    addLabel="Adicionar entrega"
                    emptyLabel="Nenhuma entrega adicionada"
                    renderItem={(item, _index, update) => (
                        <div className="grid grid-cols-5 gap-2">
                            <SelectTextField label="Cultura" id={`com-delivery-${_index}-culture`} value={item.culture}
                                onChange={e => update({ culture: e.target.value as CommodityDelivery['culture'] })}
                                items={COMMODITY_CULTURE_OPTIONS} emptyLabel="Selecione" />
                            <TextEdit label="Safra (AAAA/AAAA)" id={`com-delivery-${_index}-harvest`} value={item.harvest}
                                onChange={e => update({ harvest: e.target.value })} />
                            <TextEdit label="Sacas" id={`com-delivery-${_index}-bags`} type="number" value={item.bags ?? ''}
                                onChange={e => update({ bags: e.target.value === '' ? null : Number(e.target.value) })} />
                            <TextEdit label="Kg" id={`com-delivery-${_index}-weight`} type="number" value={item.weightKg ?? ''}
                                onChange={e => update({ weightKg: e.target.value === '' ? null : Number(e.target.value) })} />
                            <TextEdit label="Grau" id={`com-delivery-${_index}-grade`} value={item.grade ?? ''}
                                onChange={e => update({ grade: e.target.value })} />
                        </div>
                    )}
                />
                {getErrorMessage(errorFields, 'deliveries') && (
                    <div className="mt-1 text-xs text-red-600">{getErrorMessage(errorFields, 'deliveries')}</div>
                )}
            </section>

            {/* Local de formação / área */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Local de Formação da Lavoura</h4>
                <div className="grid grid-cols-3 gap-3">
                    <TextEdit label="Fazenda / Área" id="com-farm-name" value={farmName}
                        onChange={e => setFarmName(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'farmName')} />
                    <TextEdit label="Área plantada" id="com-planted-area" type="number" value={plantedArea}
                        onChange={e => setPlantedArea(e.target.value === '' ? '' : Number(e.target.value))}
                        errorMessage={getErrorMessage(errorFields, 'plantedArea')} />
                    <SelectTextField label="Unidade" id="com-area-unit" value={areaUnit}
                        onChange={e => setAreaUnit(e.target.value)}
                        items={AREA_UNIT_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'areaUnit')} />
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                    <TextEdit label="Proprietário da área" id="com-area-owner" value={areaOwner}
                        onChange={e => setAreaOwner(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'areaOwner')} />
                    <TextEdit label="Matrícula(s)" id="com-registration-number" value={registrationNumber}
                        onChange={e => setRegistrationNumber(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'registrationNumber')} />
                    <label className="flex items-center gap-2 text-sm mt-6">
                        <input type="checkbox" checked={leasedArea} onChange={e => setLeasedArea(e.target.checked)} />
                        Área arrendada
                    </label>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                    <TextEdit label="Cartório (CRI)" id="com-registry-office" value={registryOffice}
                        onChange={e => setRegistryOffice(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'registryOffice')} />
                    <SelectTextField label="UF Cartório" id="com-registry-state" value={registryState}
                        onChange={e => setRegistryState(e.target.value)}
                        items={UF_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'registryState')} />
                </div>
            </section>

            {/* Depósito */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Depósito</h4>
                <div className="grid grid-cols-3 gap-3">
                    <TextEdit label="Endereço" id="com-custody-address" value={custodyAddress}
                        onChange={e => setCustodyAddress(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'custodyAddress')} />
                    <TextEdit label="Cidade" id="com-custody-city" value={custodyCity}
                        onChange={e => setCustodyCity(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'custodyCity')} />
                    <SelectTextField label="UF" id="com-custody-state" value={custodyState}
                        onChange={e => setCustodyState(e.target.value)}
                        items={UF_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'custodyState')} />
                </div>
                <label className="flex items-center gap-2 text-sm mt-3">
                    <input type="checkbox" checked={warehouseNotified} onChange={e => setWarehouseNotified(e.target.checked)} />
                    Armazém notificado
                </label>
            </section>

            {/* Verificações de monitoramento */}
            <section>
                <RepeatableFieldList<CommodityVerification>
                    id="commodity-verifications"
                    label="Verificações de monitoramento"
                    items={verifications}
                    onAdd={addVerification}
                    onRemove={removeVerification}
                    onUpdate={updateVerification}
                    addLabel="Adicionar verificação"
                    emptyLabel="Nenhuma verificação adicionada"
                    renderItem={(item, _index, update) => (
                        <div className="grid grid-cols-5 gap-2">
                            <SelectTextField label="Tipo" id={`com-verif-${_index}-type`} value={item.verificationType}
                                onChange={e => update({ verificationType: e.target.value as CommodityVerification['verificationType'] })}
                                items={VERIFICATION_TYPE_OPTIONS} emptyLabel="Selecione" />
                            <TextEdit label="Data alerta" id={`com-verif-${_index}-scheduled`} type="date" value={item.scheduledDate ?? ''}
                                onChange={e => update({ scheduledDate: e.target.value })} />
                            <TextEdit label="Data verificação" id={`com-verif-${_index}-verified`} type="date" value={item.verifiedDate ?? ''}
                                onChange={e => update({ verifiedDate: e.target.value })} />
                            <TextEdit label="Responsável" id={`com-verif-${_index}-responsible`} value={item.responsible ?? ''}
                                onChange={e => update({ responsible: e.target.value })} />
                            <SelectTextField label="Situação" id={`com-verif-${_index}-status`} value={item.status}
                                onChange={e => update({ status: e.target.value as CommodityVerification['status'] })}
                                items={VERIFICATION_STATUS_OPTIONS} emptyLabel="Selecione" />
                        </div>
                    )}
                />
            </section>

            {/* Proprietários */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Proprietários *</h4>
                    <span className="text-xs text-gray-400">{owners.length} / {MAX_OWNERS}</span>
                </div>
                <div className="relative">
                    <TextEdit label="Nome / Razão Social (Enter para buscar)" id="com-owner-search" value={ownerSearch}
                        onChange={e => setOwnerSearch(e.target.value)}
                        onKeyDown={(e: any) => e.key === 'Enter' && searchOwner(ownerSearch)}
                        errorMessage={getErrorMessage(errorFields, 'owners')} />
                    {isSearchingOwner && <div className="text-xs text-gray-400 mt-1">Buscando...</div>}
                    {showOwnerDropdown && ownerResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-auto">
                            {ownerResults.slice(0, 8).map((p: any, idx: number) => (
                                <button type="button" key={p.personId || idx} onClick={() => handleSelectOwner(p)}
                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer text-sm">
                                    <span className="font-medium dark:text-white">{p.name}</span>
                                    {p.documentFormatted && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{p.documentFormatted}</span>}
                                    <span className="text-[10px] text-gray-400 ml-1">({p.type})</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {owners.length > 0 && (
                    <div className="space-y-1 mt-3">
                        {owners.map((o, i) => (
                            <div key={o.personId || i} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded text-xs border border-gray-100 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{o.name}</span>
                                    {o.document && <span className="text-gray-500 dark:text-gray-400">{o.document}</span>}
                                </div>
                                <button type="button" onClick={() => removeOwner(i)}
                                    className="text-red-500 hover:text-red-700 text-xs font-medium">Remover</button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Comentários */}
            <section>
                <TextEdit label="Comentários" id="com-comments" value={comments}
                    onChange={e => setComments(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'comments')} />
            </section>
        </div>
    );
};

export default CommodityForm;
